import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { ExtentDTO } from "~app/common/models/common/ExtentDTO";
import { ForeslagetKartutsnittDTO } from "~app/common/models/planera/v2/ForeslagetKartutsnittDTO";
import { ApiClientService } from "~app/common/services/api-client.service";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IMapGeometryWicket } from "../../../map.settings";
import { SpatialService } from "../../../services/spatial.service";
import { ExtentSelectorSymbolType, IForeslagetKartutsnitt, IKartutsnitt, KartutsnittTyp } from "./map-extent.models";

@Injectable({
    providedIn: "root"
})
export class MapExtentService {
    scales: number[] = [
        100000,
        75000,
        50000,
        30000,
        20000,
        10000,
        9000,
        8000,
        7000,
        6000,
        5000,
        4000,
        3000,
        2000,
        1000
    ];

    private readonly apiClientService = inject(ApiClientService);
    private readonly defaultScale = 2000;
    private readonly documentHeightLandscape = 0.1181891;
    private readonly documentHeightPortrait = 0.2366186;
    private readonly documentWidthLandscape = 0.175;
    private readonly documentWidthPortrait = 0.175;
    private readonly spatialService = inject(SpatialService);

    createNewKartutsnitt(scale: number, mapExtent, extents: IForeslagetKartutsnitt): IKartutsnitt {
        let extent;
        let offset = 0;
        do {
            offset += 50;
            extent = this.getExtent(mapExtent, this.documentWidthPortrait, this.documentHeightPortrait, scale, offset);
        } while (extents.detaljkartor.some(dk => this.isSame(dk.extent, extent)));

        return {
            id: undefined,
            guid: undefined,
            skala: this.defaultScale,
            extent: extent,
            typ: ExtentSelectorSymbolType.Detaljkarta
        };
    }

    getExtentSuggestions(arbetsorderId: number): Observable<IForeslagetKartutsnitt> {
        return this.apiClientService.get<ForeslagetKartutsnittDTO>(`arbetsorder/${arbetsorderId}/BeraknaKartutsnitt`);
    }

    getExtentsForArbetsorder(arbetsorderId: number): Observable<IForeslagetKartutsnitt> {
        return this.apiClientService.get<ForeslagetKartutsnittDTO>(`arbetsorder/${arbetsorderId}/HamtaForeslagetKartutsnitt`);
    }

    getGeometryWicketFromExtent(kartutsnitt: IKartutsnitt): IMapGeometryWicket {
        return {
            attributes: { id: kartutsnitt.guid },
            wkt: this.spatialService.extentToWktPolygon(kartutsnitt.extent),
            id: kartutsnitt.guid ?? GuidUtils.newGuid(),
            tag: kartutsnitt,
            color: [
                255,
                255,
                255,
                0.2
            ],
            styleName: this.getExtentStyle(kartutsnitt),
            visible: true
        };
    }

    getScaleFromWkt(wkt: any): number {
        const extent = this.spatialService.wktPolygonToExtent(wkt);
        const isLandscape = (extent.maxX - extent.minX) > (extent.maxY - extent.minY);

        return (extent.maxX - extent.minX) / (isLandscape ? this.documentWidthLandscape : this.documentWidthPortrait);
    }

    isSame(extent1: ExtentDTO, extent2: ExtentDTO) {
        return extent1.maxX === extent2.maxX &&
            extent1.maxY === extent2.maxY &&
            extent1.minX === extent2.minX &&
            extent1.minY === extent2.minY;
    }

    resizeKartutsnittToScale(kartutsnitt: IKartutsnitt, scale: number) {
        const centerPoint = this.getCenter(kartutsnitt.extent.minX, kartutsnitt.extent.minY, kartutsnitt.extent.maxX, kartutsnitt.extent.maxY);

        const isPortrait = kartutsnitt.typ === KartutsnittTyp.Detaljkarta;
        const width = scale * (isPortrait ? this.documentWidthPortrait : this.documentWidthLandscape);
        const height = scale * (isPortrait ? this.documentHeightPortrait : this.documentHeightLandscape);

        kartutsnitt.extent.minX = centerPoint[0] - width / 2;
        kartutsnitt.extent.maxX = centerPoint[0] + width / 2;
        kartutsnitt.extent.minY = centerPoint[1] - height / 2;
        kartutsnitt.extent.maxY = centerPoint[1] + height / 2;
        kartutsnitt.skala = scale;
    }

    updateKartutsnittFromWkt(kartutsnitt: IKartutsnitt, wkt): boolean {
        const extentBefore = ObjectUtils.deepCopy(kartutsnitt.extent);
        kartutsnitt.extent = this.spatialService.wktPolygonToExtent(wkt);
        kartutsnitt.skala = Math.round(this.getScaleFromWkt(wkt));
        return !this.isSame(extentBefore, kartutsnitt.extent);
    }

    private getCenter(minX: number, minY: number, maxX: number, maxY: number): [number, number] {
        const x = (maxX - minX) / 2;
        const y = (maxY - minY) / 2;

        return [
            minX + x,
            minY + y
        ];
    }

    private getExtent(mapExtent: any, imgWidthInMeters: number, imgHeightInMeters: number, scale: number, offset = 0) {
        // Calculating half of map’s height & width at the specific scale
        const dX = imgWidthInMeters * scale / 2;
        const dY = imgHeightInMeters * scale / 2;

        const xyPoint = this.getCenter(mapExtent.xmin, mapExtent.ymin, mapExtent.xmax, mapExtent.ymax);

        const minX = xyPoint[0] - dX;
        const maxX = xyPoint[0] + dX;
        const minY = xyPoint[1] - dY;
        const maxY = xyPoint[1] + dY;

        return {
            minX: minX + offset,
            minY: minY - offset,
            maxX: maxX + offset,
            maxY: maxY - offset
        };
    }

    private getExtentStyle(extent: IKartutsnitt) {
        switch (extent.typ) {
            case KartutsnittTyp.Detaljkarta:
                return "extentDetail";
            case KartutsnittTyp.Oversiktskarta1:
                return "extentOverview1";
            case KartutsnittTyp.Oversiktskarta2:
                return "extentOverview2";
            default:
                throw new Error(`Ogiltigt kartutsnittstyp ${extent?.typ}'`);
        }
    }
}
